import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Loader2 } from 'lucide-react';
import {
  trackingEventSchema,
  type TrackingEventFormData,
} from '../schemas/import-tracking.schemas';
import { useImportTrackingStore } from '../store/import-tracking-store';
import { importTrackingService } from '../services/import-tracking-service';
import type { ImportOrderStatus, ImportOrderLeg } from '../types/import-tracking.types';

interface TrackingEventFormDialogProps {
  orderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  /**
   * Estado actual de la orden, para arrancar el desplegable ahí.
   *
   * 🔴 Antes arrancaba fijo en 'CONFIRMED'. Como el evento **arrastra el estado**
   * de la orden, registrar un movimiento de una carga que ya estaba EN TRÁNSITO
   * la hacía retroceder a Confirmada sin que nadie lo pidiera — y con eso se
   * deshabilitaban Documentos y Seguimiento, que dependen de que la carga salió.
   *
   * Lo encontró una prueba E2E: registraba un evento y la pestaña desde la que
   * lo había registrado desaparecía.
   *
   * Arrancar en el estado actual hace que registrar un movimiento sea neutro por
   * defecto: mueve el estado solo si la persona lo cambia a propósito.
   */
  currentStatus?: ImportOrderStatus;
}

/**
 * Con qué estado arranca el desplegable.
 *
 * El estado actual de la orden, si es uno de los que el formulario ofrece. Una
 * COTIZACIÓN no tiene movimientos —todavía no hay carga— así que ahí se cae a
 * "Confirmada", que es el primer estado con seguimiento real.
 */
function estadoInicial(actual?: ImportOrderStatus): TrackingEventFormData['status'] {
  const ofrecidos = STATUS_OPTIONS.map((o) => o.value);
  return actual && ofrecidos.includes(actual)
    ? (actual as TrackingEventFormData['status'])
    : 'CONFIRMED';
}

const STATUS_OPTIONS: { value: ImportOrderStatus; label: string }[] = [
  { value: 'CONFIRMED', label: 'Confirmada' },
  { value: 'IN_TRANSIT', label: 'En tránsito' },
  { value: 'DELIVERED', label: 'Entregada' },
  { value: 'CANCELLED', label: 'Cancelada' },
];

const STATUS_TITLE_SUGGESTIONS: Record<string, string> = {
  CONFIRMED: 'Orden confirmada por proveedor',
  IN_TRANSIT: 'Envío en tránsito',
  DELIVERED: 'Mercadería entregada',
  CANCELLED: 'Orden cancelada',
};

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

export function TrackingEventFormDialog({
  orderId,
  open,
  onOpenChange,
  onSuccess,
  currentStatus,
}: TrackingEventFormDialogProps) {
  const { addTrackingEvent } = useImportTrackingStore();

  const [legs, setLegs] = useState<ImportOrderLeg[]>([]);
  const [legId, setLegId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<TrackingEventFormData>({
    resolver: zodResolver(trackingEventSchema),
    defaultValues: {
      status: estadoInicial(currentStatus),
      title: '',
      description: '',
      location: '',
      eventDate: todayISO(),
    },
  });

  const selectedStatus = watch('status');
  const currentTitle = watch('title');

  // Fetch order legs when dialog opens
  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    importTrackingService
      .getOrderLegs(orderId)
      .then((data) => {
        if (!cancelled) setLegs(data);
      })
      .catch(() => {
        if (!cancelled) setLegs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      reset({
        // El mismo criterio que el default: arranca en el estado actual de la
        // orden. Acá estaba fijo en CONFIRMED y pisaba el valor correcto cada vez
        // que se abría el diálogo — por eso el arreglo del default no alcanzaba.
        status: estadoInicial(currentStatus),
        title: '',
        description: '',
        location: '',
        eventDate: todayISO(),
      });
      setLegId(null);
    }
  }, [open, reset, currentStatus]);

  // Auto-suggest title when status changes (only if title is empty or matches a previous suggestion)
  useEffect(() => {
    const previousSuggestions = Object.values(STATUS_TITLE_SUGGESTIONS);
    if (!currentTitle || previousSuggestions.includes(currentTitle)) {
      const suggestion = STATUS_TITLE_SUGGESTIONS[selectedStatus];
      if (suggestion) {
        setValue('title', suggestion);
      }
    }
  }, [selectedStatus, setValue, currentTitle]);

  const onSubmit = async (data: TrackingEventFormData) => {
    try {
      await addTrackingEvent(orderId, { ...data, legId: legId ?? undefined });
      onOpenChange(false);
      onSuccess?.();
    } catch {
      // Error is handled by the store
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Registrar Evento de Seguimiento</DialogTitle>
          <DialogDescription>
            Registre un nuevo evento en el historial de seguimiento de esta orden
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label>Estado *</Label>
            <SearchableSelect
              options={STATUS_OPTIONS}
              value={selectedStatus}
              onValueChange={(v) => setValue('status', v as TrackingEventFormData['status'])}
              placeholder="Seleccionar estado..."
              searchPlaceholder="Buscar estado..."
            />
            {errors.status && (
              <p className="text-xs text-destructive mt-1">{errors.status.message}</p>
            )}
          </div>

          {legs.length > 0 && (
            <div className="space-y-2">
              <Label>Tramo (opcional)</Label>
              <Select
                value={legId ?? '__NONE__'}
                onValueChange={(v) => setLegId(v === '__NONE__' ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sin tramo específico" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__NONE__">Sin tramo específico</SelectItem>
                  {legs.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.legOrder}. {l.origin} → {l.destination} ({l.transportMode})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="Descripción breve del evento..."
            />
            {errors.title && (
              <p className="text-xs text-destructive mt-1">{errors.title.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="eventDate">Fecha del Evento *</Label>
            <Input id="eventDate" type="date" {...register('eventDate')} />
            {errors.eventDate && (
              <p className="text-xs text-destructive mt-1">{errors.eventDate.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="location">Ubicación</Label>
            <Input
              id="location"
              {...register('location')}
              placeholder="Ej: Puerto de Shanghai, China"
            />
          </div>

          <div>
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Detalles adicionales del evento..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Registrar Evento
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
